import type { Control } from "react-hook-form";
import type { PlanDetailResponse } from "../../state/api.types";
import { formatCents } from "../../utilities/currency";
import { ChangeDetailsElement } from "./ChangeDetailsElement";
import { useFormValues, type FormValues } from "./common";
import type { RenewalPlanOption } from "./renewalPlan";

type ChangeDetailsProps = {
  plan: PlanDetailResponse;
  control: Control<FormValues>;
  renewalPlanOptions: RenewalPlanOption[];
};

export const ChangeDetails = ({
  plan,
  control,
  renewalPlanOptions,
}: ChangeDetailsProps) => {
  const formValues = useFormValues(control);

  return (
    <div className="flex flex-col gap-2xl">
      {formValues.name.fieldState.isDirty && (
        <ChangeDetailsElement
          title="Plan name"
          oldValue={plan.name}
          newValue={formValues.name.field.value}
        />
      )}
      {formValues.description.fieldState.isDirty && (
        <ChangeDetailsElement
          title="Internal description"
          oldValue={plan.description ?? ""}
          newValue={formValues.description.field.value ?? ""}
        />
      )}
      {formValues.termMonths.fieldState.isDirty && (
        <ChangeDetailsElement
          title="Default term"
          oldValue={`${plan.term_months.toString()} months`}
          newValue={`${formValues.termMonths.field.value} months`}
        />
      )}
      {formValues.baseFee.fieldState.isDirty && (
        <ChangeDetailsElement
          title="Monthly base fee"
          oldValue={`$${plan.plan_monthly_cost_dollars ?? "0.00"}`}
          newValue={`$${formValues.baseFee.field.value}`}
        />
      )}
      {formValues.monthlyCommission.fieldState.isDirty && (
        <ChangeDetailsElement
          title="Default monthly commission"
          oldValue={`$${plan.partner_commission_per_month_dollars ?? "0.00"}`}
          newValue={`$${formValues.monthlyCommission.field.value}`}
        />
      )}
      {formValues.exportRate.fieldState.isDirty && (
        <ChangeDetailsElement
          title="Buyback rate"
          oldValue={`${formatCents(parseFloat(plan.export_rate_kwh_dollars ?? "0") * 100)}¢`}
          newValue={`${formatCents(parseFloat(formValues.exportRate.field.value))}¢`}
        />
      )}
      {formValues.renewalPlanUuid.fieldState.isDirty && (
        <ChangeDetailsElement
          title="Default renewal plan"
          oldValue={
            plan.renewal_plan_uuid === null
              ? "Current plan"
              : (plan.renewal_plan_name ?? plan.renewal_plan_uuid ?? "")
          }
          newValue={
            renewalPlanOptions.find(
              (p) => p.uuid === formValues.renewalPlanUuid.field.value,
            )?.name ?? "Current plan"
          }
        />
      )}
    </div>
  );
};
